/**
 * The last N lines a running configuration printed, as plain text.
 *
 * PTY output arrives in arbitrary chunks with ANSI colour codes, cursor
 * moves and `\r` progress redraws (`npm` spinners, Vite's "ready in"). The
 * Run section shows a log, not a terminal, so escapes are stripped, a lone
 * `\r` rewrites the current line, and only the last `max` lines are kept.
 */

export const MAX_LOG_LINES = 500;
const MAX_LINE_CHARS = 4000;

// CSI (`ESC [ … final`), OSC (`ESC ] … BEL|ESC \`), two-char escapes, and the
// remaining C0 controls except \n, \r and \t.
// eslint-disable-next-line no-control-regex
const ANSI_RE = /\x1b\[[0-?]*[ -/]*[@-~]|\x1b\][^\x07\x1b]*(?:\x07|\x1b\\)|\x1b[@-Z\\-_]|[\x00-\x08\x0b\x0c\x0e-\x1f\x7f]/g;
// eslint-disable-next-line no-control-regex
const COMPLETE_ESC_RE = /^\x1b(?:\[[0-?]*[ -/]*[@-~]|\][^\x07\x1b]*(?:\x07|\x1b\\)|[@-Z\\-_])/;

export function stripAnsi(s) {
  return String(s).replace(ANSI_RE, '');
}

/** An empty buffer: finished lines plus the line still being written. */
export function createLogBuffer() {
  return { lines: [], partial: '', pendingEsc: '' };
}

const clip = (s) => (s.length > MAX_LINE_CHARS ? s.slice(0, MAX_LINE_CHARS) : s);

/**
 * Append one chunk. Returns a NEW buffer (store-friendly); the input is not
 * changed. An escape sequence split across two chunks is held back until
 * its end arrives.
 */
export function appendLog(buf, chunk, max = MAX_LOG_LINES) {
  let text = buf.pendingEsc + String(chunk);
  let pendingEsc = '';
  const lastEsc = text.lastIndexOf('\x1b');
  // An unterminated sequence at the very end: keep it for the next chunk.
  if (lastEsc !== -1 && text.length - lastEsc < 64 && !COMPLETE_ESC_RE.test(text.slice(lastEsc))) {
    pendingEsc = text.slice(lastEsc);
    text = text.slice(0, lastEsc);
  }
  const clean = stripAnsi(text).replace(/\r\n/g, '\n');
  const parts = clean.split('\n');
  const lines = buf.lines.slice();
  let partial = buf.partial;
  parts.forEach((part, i) => {
    // `\r` without `\n`: the program redraws the line — keep what follows the last one.
    const cr = part.lastIndexOf('\r');
    partial = cr === -1 ? clip(partial + part) : clip(part.slice(cr + 1));
    if (i < parts.length - 1) {
      lines.push(partial);
      partial = '';
    }
  });
  const overflow = lines.length + (partial ? 1 : 0) - max;
  if (overflow > 0) lines.splice(0, overflow);
  return { lines, partial, pendingEsc };
}

/** Every visible line, the unfinished one last. */
export function logLines(buf) {
  return buf.partial ? [...buf.lines, buf.partial] : buf.lines;
}
